/**
 * Email Management Page
 * AIDLC Administrative Backend
 *
 * Single page at /email with 3 tabs:
 *   1. Inbox - Read/reply to emails from integrated mailboxes
 *   2. Signature - Configure the LDAJA email signature template
 *   3. OneDrive - Browse OneDrive files for attachments
 *
 * Mailbox selector at top switches between integrated accounts.
 * All Graph API calls go through the backend.
 *
 * Personas: ops
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import * as emailService from '../../services/emailService';
import type {
  InboxMessage,
  EmailSignatureTemplate,
  EmailIntegrationConfig,
  OneDriveItem,
} from '../../types/email';

// ── Tab Type ──────────────────────────────────────────────────────

type Tab = 'inbox' | 'signature' | 'onedrive';

// ── Main Page ─────────────────────────────────────────────────────

export function EmailManagementPage() {
  const [activeTab, setActiveTab] = useState<Tab>('inbox');
  const [integrations, setIntegrations] = useState<EmailIntegrationConfig[]>([]);
  const [selectedMailbox, setSelectedMailbox] = useState<string>('');
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  // Load integrations on mount
  useEffect(() => {
    emailService.getIntegrations()
      .then((data) => {
        setIntegrations(data);
        if (data.length > 0 && !selectedMailbox) {
          setSelectedMailbox(data[0].userEmail);
        }
      })
      .catch(() => {
        // Integrations are store-only, should always work
        setIntegrations([]);
      })
      .finally(() => setLoadingIntegrations(false));
  }, []);

  const selectedIntegration = useMemo(
    () => integrations.find((i) => i.userEmail === selectedMailbox) ?? null,
    [integrations, selectedMailbox]
  );

  // If active tab is disabled by feature flag, reset to first enabled tab
  useEffect(() => {
    if (!selectedIntegration) return;
    const featureForTab: Record<Tab, keyof typeof selectedIntegration.features> = {
      inbox: 'inbox',
      signature: 'signature',
      onedrive: 'onedrive',
    };
    if (!selectedIntegration.features[featureForTab[activeTab]]) {
      const firstEnabled = (['inbox', 'signature', 'onedrive'] as Tab[]).find(
        (t) => selectedIntegration.features[featureForTab[t]]
      );
      if (firstEnabled) setActiveTab(firstEnabled);
    }
  }, [selectedIntegration, activeTab]);

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'inbox',
      label: 'Inbox',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
        </svg>
      ),
    },
    {
      key: 'signature',
      label: 'Signature',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
        </svg>
      ),
    },
    {
      key: 'onedrive',
      label: 'OneDrive',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage inbox, signature template, and OneDrive files
          </p>
        </div>

        {/* Mailbox Selector */}
        {integrations.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Mailbox:</label>
            <select
              value={selectedMailbox}
              onChange={(e) => setSelectedMailbox(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            >
              {integrations.map((i) => (
                <option key={i.id} value={i.userEmail}>
                  {i.displayName} ({i.userEmail})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const featureDisabled = selectedIntegration
              ? !selectedIntegration.features[tab.key]
              : false;
            return (
              <button
                key={tab.key}
                onClick={() => !featureDisabled && setActiveTab(tab.key)}
                disabled={featureDisabled}
                className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  featureDisabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : activeTab === tab.key
                      ? 'border-ldaja-blue text-ldaja-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title={featureDisabled ? `${tab.label} is disabled for this mailbox` : undefined}
              >
                {tab.icon}
                {tab.label}
                {featureDisabled && (
                  <span className="text-[10px] text-gray-400 ml-1">OFF</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {loadingIntegrations ? (
        <LoadingSpinner message="Loading email configuration..." />
      ) : (
        <>
          {activeTab === 'inbox' && (
            <InboxTab userEmail={selectedMailbox} />
          )}
          {activeTab === 'signature' && (
            <SignatureTab userEmail={selectedMailbox} integrations={integrations} />
          )}
          {activeTab === 'onedrive' && (
            <OneDriveTab userEmail={selectedMailbox} />
          )}
        </>
      )}

      {/* Integration Management */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <IntegrationManager
          integrations={integrations}
          onUpdate={setIntegrations}
          onSelect={setSelectedMailbox}
        />
      </div>
    </div>
  );
}

// ── Inbox Tab ─────────────────────────────────────────────────────

function InboxTab({ userEmail }: { userEmail: string }) {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<InboxMessage | null>(null);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const loadMessages = useCallback(async () => {
    if (!userEmail) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await emailService.getInboxMessages(userEmail, pageSize, page * pageSize);
      setMessages(result.messages);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load inbox';
      if (msg.includes('503') || msg.includes('not configured')) {
        setError('Graph email not configured. Add GRAPH_CLIENT_SECRET and GRAPH_SENDER_EMAIL to the backend environment, and grant Mail.Read permission in Entra ID.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [userEmail, page]);

  // Reset page when mailbox changes
  useEffect(() => {
    setPage(0);
    setSelectedMessage(null);
  }, [userEmail]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleSelectMessage = async (msg: InboxMessage) => {
    setLoadingMessage(true);
    setReplyMode(false);
    setReplyBody('');
    try {
      const full = await emailService.getInboxMessage(userEmail, msg.id);
      setSelectedMessage(full);
      if (!msg.isRead) {
        await emailService.markMessageRead(userEmail, msg.id);
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, isRead: true } : m))
        );
      }
    } catch {
      setSelectedMessage(msg);
    } finally {
      setLoadingMessage(false);
    }
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyBody.trim()) return;
    setSending(true);
    setReplyError(null);
    try {
      await emailService.replyToMessage(userEmail, selectedMessage.id, replyBody);
      setReplyMode(false);
      setReplyBody('');
      setSelectedMessage(null);
    } catch {
      setReplyError('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!userEmail) {
    return (
      <div className="text-center py-12 text-gray-500">
        No mailbox selected. Add an integration below to get started.
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Loading inbox..." />;

  if (error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <svg className="w-8 h-8 text-yellow-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <p className="text-sm text-yellow-800 font-medium mb-1">Inbox Not Available</p>
        <p className="text-xs text-yellow-700">{error}</p>
      </div>
    );
  }

  // Split view: message list + detail
  return (
    <div className="flex gap-4" style={{ minHeight: '500px' }}>
      {/* Message List */}
      <div className="w-1/3 border border-gray-200 rounded-lg overflow-hidden flex flex-col">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Inbox ({messages.length} messages)
          </span>
          <button
            onClick={loadMessages}
            className="text-xs text-ldaja-blue hover:underline"
          >
            Refresh
          </button>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {messages.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No messages</div>
          ) : (
            messages.map((msg) => (
              <button
                key={msg.id}
                onClick={() => handleSelectMessage(msg)}
                className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 ${
                  selectedMessage?.id === msg.id ? 'bg-blue-50' : ''
                } ${!msg.isRead ? 'bg-white' : 'bg-gray-50/50'}`}
              >
                <div className="flex items-center gap-2">
                  {!msg.isRead && (
                    <span className="w-2 h-2 rounded-full bg-ldaja-blue flex-shrink-0" />
                  )}
                  <span className={`text-sm truncate ${!msg.isRead ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                    {msg.from.emailAddress.name || msg.from.emailAddress.address}
                  </span>
                </div>
                <p className={`text-sm mt-0.5 truncate ${!msg.isRead ? 'font-medium text-gray-800' : 'text-gray-500'}`}>
                  {msg.subject || '(no subject)'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-gray-400">
                    {new Date(msg.receivedDateTime).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </span>
                  {msg.hasAttachments && (
                    <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                    </svg>
                  )}
                  {msg.importance === 'high' && <Badge variant="danger">!</Badge>}
                </div>
              </button>
            ))
          )}
        </div>
        {/* Pagination */}
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex items-center justify-between">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="text-xs text-ldaja-blue hover:underline disabled:text-gray-300 disabled:no-underline"
          >
            Previous
          </button>
          <span className="text-xs text-gray-400">Page {page + 1}</span>
          <button
            disabled={messages.length < pageSize}
            onClick={() => setPage((p) => p + 1)}
            className="text-xs text-ldaja-blue hover:underline disabled:text-gray-300 disabled:no-underline"
          >
            Next
          </button>
        </div>
      </div>

      {/* Message Detail / Reply */}
      <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden flex flex-col">
        {loadingMessage ? (
          <LoadingSpinner message="Loading message..." size="sm" />
        ) : selectedMessage ? (
          <>
            {/* Header */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{selectedMessage.subject || '(no subject)'}</h2>
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                <span>
                  <strong>From:</strong> {selectedMessage.from.emailAddress.name}{' '}
                  &lt;{selectedMessage.from.emailAddress.address}&gt;
                </span>
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {new Date(selectedMessage.receivedDateTime).toLocaleString()}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {selectedMessage.body ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedMessage.body.content) }}
                />
              ) : (
                <p className="text-gray-600">{selectedMessage.bodyPreview}</p>
              )}
            </div>

            {/* Reply Section */}
            <div className="border-t border-gray-200 px-6 py-4">
              {replyMode ? (
                <div>
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type your reply..."
                    rows={5}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent resize-none"
                  />
                  {replyError && (
                    <p className="text-sm text-red-600 mt-2">{replyError}</p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-gray-400">Signature will be appended automatically</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setReplyMode(false); setReplyBody(''); }}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleReply}
                        disabled={!replyBody.trim() || sending}
                        className="px-4 py-1.5 text-sm bg-ldaja-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {sending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setReplyMode(true)}
                    className="px-4 py-1.5 text-sm bg-ldaja-blue text-white rounded-md hover:bg-blue-700 flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                    Reply
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Select a message to view
          </div>
        )}
      </div>
    </div>
  );
}

// ── Signature Tab ─────────────────────────────────────────────────

function SignatureTab({ userEmail, integrations }: { userEmail: string; integrations: EmailIntegrationConfig[] }) {
  const [template, setTemplate] = useState<EmailSignatureTemplate | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadSignature = useCallback(async () => {
    if (!userEmail) { setLoading(false); return; }
    setLoading(true);
    setSaved(false);
    setSaveError(null);
    try {
      const [tmpl, html] = await Promise.all([
        emailService.getSignatureTemplate(userEmail),
        emailService.getSignaturePreview(userEmail),
      ]);
      setTemplate(tmpl);
      setPreview(html);
    } catch {
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    loadSignature();
  }, [loadSignature]);

  const handleSave = async () => {
    if (!template || !userEmail) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const updated = await emailService.updateSignatureTemplate(userEmail, template);
      setTemplate(updated);
      const html = await emailService.getSignaturePreview(userEmail);
      setPreview(html);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaveError('Failed to save signature template.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof EmailSignatureTemplate, value: string) => {
    setTemplate((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  if (!userEmail) {
    return (
      <div className="text-center py-12 text-gray-500">
        No mailbox selected. Add an integration below to get started.
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Loading signature template..." />;
  if (!template) return <div className="text-center py-8 text-gray-500">Failed to load signature template</div>;

  const currentIntegration = integrations.find(i => i.userEmail === userEmail);

  return (
    <div className="grid grid-cols-2 gap-8">
      {/* Editor */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Signature Template</h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {currentIntegration?.displayName || userEmail}
          </span>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valediction (sign-off)</label>
            <input
              type="text"
              value={template.valediction}
              onChange={(e) => updateField('valediction', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
              placeholder="Onward and upward,"
            />
            <p className="text-xs text-gray-400 mt-1">Agent-generated sign-off (can be overridden per reply)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Signer Name</label>
            <input
              type="text"
              value={template.signerName}
              onChange={(e) => updateField('signerName', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={template.signerTitle}
              onChange={(e) => updateField('signerTitle', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={template.signerEmail}
              onChange={(e) => updateField('signerEmail', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <input
              type="text"
              value={template.tagline}
              onChange={(e) => updateField('tagline', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm bg-ldaja-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Saved
              </span>
            )}
            {saveError && (
              <span className="text-sm text-red-600">{saveError}</span>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <div className="mb-4 pb-4 border-b border-gray-100">
            <p className="text-sm text-gray-500 mb-2">This is what your signature looks like in an email:</p>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            <p>Hi [Recipient],</p>
            <br />
            <p>Thank you for reaching out. I wanted to follow up on our conversation...</p>
            <br />
          </div>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview) }} />
        </div>
      </div>
    </div>
  );
}

// ── OneDrive Tab ──────────────────────────────────────────────────

function OneDriveTab({ userEmail }: { userEmail: string }) {
  const [files, setFiles] = useState<OneDriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([]);

  const loadFiles = useCallback(async (folderId?: string) => {
    if (!userEmail) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const items = await emailService.getOneDriveFiles(userEmail, folderId);
      setFiles(items);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load files';
      if (msg.includes('503') || msg.includes('not configured')) {
        setError('Graph not configured. Add GRAPH_CLIENT_SECRET to the backend and grant Files.Read.All permission.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    setBreadcrumbs([]);
    loadFiles();
  }, [userEmail, loadFiles]);

  const navigateToFolder = (item: OneDriveItem) => {
    setBreadcrumbs((prev) => [...prev, { id: item.id, name: item.name }]);
    loadFiles(item.id);
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setBreadcrumbs([]);
      loadFiles();
    } else {
      const target = breadcrumbs[index];
      setBreadcrumbs((prev) => prev.slice(0, index + 1));
      loadFiles(target.id);
    }
  };

  const handleDownload = async (item: OneDriveItem) => {
    try {
      const url = await emailService.getOneDriveFileDownloadUrl(userEmail, item.id);
      if (url) window.open(url, '_blank');
    } catch {
      // Silent fail
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  if (!userEmail) {
    return (
      <div className="text-center py-12 text-gray-500">
        No mailbox selected. Add an integration below to get started.
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <svg className="w-8 h-8 text-yellow-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <p className="text-sm text-yellow-800 font-medium mb-1">OneDrive Not Available</p>
        <p className="text-xs text-yellow-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1.5 mb-4 text-sm">
        <button
          onClick={() => navigateToBreadcrumb(-1)}
          className="text-ldaja-blue hover:underline font-medium"
        >
          Root
        </button>
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.id} className="flex items-center gap-1.5">
            <span className="text-gray-400">/</span>
            <button
              onClick={() => navigateToBreadcrumb(i)}
              className={`${i === breadcrumbs.length - 1 ? 'text-gray-700 font-medium' : 'text-ldaja-blue hover:underline'}`}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner message="Loading files..." size="sm" />
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 w-24">Size</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 w-40">Modified</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {files.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-400">
                    No files in this folder
                  </td>
                </tr>
              ) : (
                files.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      {item.folder ? (
                        <button
                          onClick={() => navigateToFolder(item)}
                          className="flex items-center gap-2 text-ldaja-blue hover:underline"
                        >
                          <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          {item.name}
                          <span className="text-xs text-gray-400">({item.folder.childCount})</span>
                        </button>
                      ) : (
                        <span className="flex items-center gap-2 text-gray-700">
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                          </svg>
                          {item.name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {item.file ? formatFileSize(item.size) : '--'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {new Date(item.lastModifiedDateTime).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-2.5">
                      {item.file && (
                        <button
                          onClick={() => handleDownload(item)}
                          className="text-ldaja-blue hover:text-blue-700"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Integration Manager ───────────────────────────────────────────

function FeatureToggle({
  label,
  enabled,
  onChange,
}: {
  label: string;
  enabled: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
          enabled ? 'bg-ldaja-blue' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </button>
      <span className="text-[11px] text-gray-600">{label}</span>
    </label>
  );
}

function IntegrationManager({
  integrations,
  onUpdate,
  onSelect,
}: {
  integrations: EmailIntegrationConfig[];
  onUpdate: (configs: EmailIntegrationConfig[]) => void;
  onSelect: (email: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const extractError = (err: unknown): string => {
    const resp = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    return resp ?? 'An unexpected error occurred';
  };

  const handleAdd = async () => {
    if (!newEmail || !newName) return;
    setError(null);
    try {
      const created = await emailService.addIntegration({ userEmail: newEmail, displayName: newName });
      onUpdate([...integrations, created]);
      onSelect(created.userEmail);
      setAdding(false);
      setNewEmail('');
      setNewName('');
    } catch (err) {
      setError(extractError(err));
    }
  };

  const handleRemove = async (id: string) => {
    setError(null);
    try {
      await emailService.removeIntegration(id);
      const updated = integrations.filter((i) => i.id !== id);
      onUpdate(updated);
      if (updated.length > 0) onSelect(updated[0].userEmail);
    } catch (err) {
      setError(extractError(err));
    }
    setConfirmDeleteId(null);
  };

  const handleToggleProtected = async (integration: EmailIntegrationConfig) => {
    setError(null);
    try {
      const updated = await emailService.updateIntegration(integration.id, {
        protected: !integration.protected,
      });
      onUpdate(integrations.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setError(extractError(err));
    }
  };

  const handleToggleFeature = async (
    integration: EmailIntegrationConfig,
    feature: 'inbox' | 'signature' | 'onedrive',
    value: boolean
  ) => {
    setError(null);
    try {
      const updated = await emailService.updateIntegration(integration.id, {
        features: { ...integration.features, [feature]: value },
      });
      onUpdate(integrations.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setError(extractError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Integrated Mailboxes</h3>
        <button
          onClick={() => setAdding((v) => !v)}
          className="text-xs text-ldaja-blue hover:underline"
        >
          {adding ? 'Cancel' : '+ Add Mailbox'}
        </button>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
          <p className="text-xs text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {adding && (
        <div className="flex items-end gap-3 mb-4 bg-gray-50 p-4 rounded-lg">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@ldaja.com"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Dan Dagnall"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={!newEmail || !newName}
            className="px-4 py-1.5 text-sm bg-ldaja-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
          >
            Add
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="bg-white border border-gray-200 rounded-lg px-4 py-3"
          >
            {/* Header row: name + actions */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{integration.displayName}</p>
                  <p className="text-xs text-gray-500 truncate">{integration.userEmail}</p>
                </div>
                {integration.protected && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded border border-amber-200 shrink-0">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    Protected
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Protection toggle */}
                <button
                  onClick={() => handleToggleProtected(integration)}
                  className={`p-1 rounded transition-colors ${
                    integration.protected
                      ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}
                  title={integration.protected ? 'Remove protection' : 'Protect mailbox'}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    {integration.protected ? (
                      <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    ) : (
                      <path d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    )}
                  </svg>
                </button>
                {/* Delete button — hidden for protected integrations */}
                {!integration.protected && (
                  <button
                    onClick={() => setConfirmDeleteId(integration.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
                    title="Remove"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Feature toggles */}
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <FeatureToggle
                label="Inbox"
                enabled={integration.features?.inbox ?? true}
                onChange={(v) => handleToggleFeature(integration, 'inbox', v)}
              />
              <FeatureToggle
                label="Signature"
                enabled={integration.features?.signature ?? true}
                onChange={(v) => handleToggleFeature(integration, 'signature', v)}
              />
              <FeatureToggle
                label="OneDrive"
                enabled={integration.features?.onedrive ?? true}
                onChange={(v) => handleToggleFeature(integration, 'onedrive', v)}
              />
            </div>

            {/* Delete confirmation overlay */}
            {confirmDeleteId === integration.id && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-700 mb-2">
                  Delete <strong>{integration.displayName}</strong>? This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRemove(integration.id)}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-3 py-1 text-xs bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
